/**
 * useAppProject Hook
 *
 * Manages state for a single DC project (fetch, update, auto-save).
 */

import { useState, useEffect, useCallback } from 'react';
import { getProject, updateProject } from '../services/appService';

/**
 * Hook for managing a single DC project
 *
 * @param {string} projectId - Project ID
 * @returns {Object} { project, loading, error, updateProject, refresh }
 */
export function useAppProject(projectId) {
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchProject = useCallback(async () => {
    if (!projectId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await getProject(projectId);
      setProject(data);
    } catch (err) {
      console.error('Failed to fetch DC project:', err);
      setError(err.message || 'Failed to load project');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  const handleUpdateProject = useCallback(
    async (updates) => {
      if (!projectId) return;

      try {
        setError(null);
        const updated = await updateProject(projectId, updates);
        setProject(updated);
        return updated;
      } catch (err) {
        console.error('Failed to update DC project:', err);
        setError(err.message || 'Failed to update project');
        throw err;
      }
    },
    [projectId]
  );

  useEffect(() => {
    fetchProject();
  }, [fetchProject]);

  return {
    project,
    loading,
    error,
    updateProject: handleUpdateProject,
    refresh: fetchProject,
  };
}

export default useAppProject;
